import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
    getVendorAssessmentService, submitVendorAssessmentService, getAssessmentResponsesService,
    getAssessmentDetailsService, reviewResponseService, updateAssessmentReviewStatusService,
    uploadVendorDocumentsService, getVendorQuestionSuggestionsService, getTprmAssessmentsService,
    uploadVendorResponseDocumentsService, deleteVendorResponseDocumentsService,
    getAssessmentResponseDocumentService, getVendorResponseDocumentService,
} from '../../services';
import FullPageLoader from '../../Components/FullPageLoader';
import { formatText } from '../../helper';
import CommonTable from '../../Components/CommonTable';
import CommonButton from '../../Components/CommonButton';
import CommonModal from '../../Components/CommonModal';
import CommonDropdown from '../../Components/CommonDropdown';
import StatusBadge from '../../Components/StatusBadge';
import CommonActionMenu from '../../Components/CommonActionMenu';
import ButtonLoader from '../../Components/ButtonLoader';
import { useConfirm } from '../../Components/ConfirmModal';
import { gradeTone } from '../../TpcrmPortal/tpcrmUtils';
import '../../styles/VendorAssessmentView.scss';

const toOptions = (values, allLabel) => [
    { value: 'All', label: allLabel || 'All' },
    ...values.filter((v) => v !== 'All').map((v) => ({ value: v, label: v })),
];

const ACCEPTANCE_STATUS_OPTIONS = [
    { value: 'All', label: 'Status' },
    { value: 'Accepted', label: 'Accepted' },
    { value: 'Rejected', label: 'Rejected' },
    { value: 'Follow Up', label: 'Follow Up' },
    { value: 'Pending', label: 'Pending' },
];

const COMPLETION_OPTIONS = [
    { value: 'All', label: 'Completion' },
    { value: 'Answered', label: 'Answered' },
    { value: 'Unanswered', label: 'Unanswered' },
];

const REVIEW_ACCEPTANCE_OPTIONS = [
    { value: 'pending', label: 'Pending' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'follow_up', label: 'Follow Up' },
];

const GRADE_OPTIONS = [
    { value: '', label: 'Select Grade' },
    { value: 'A', label: 'A' },
    { value: 'B', label: 'B' },
    { value: 'C', label: 'C' },
    { value: 'D', label: 'D' },
    { value: 'F', label: 'F' },
];

const STATUS_PICKER_OPTIONS = [
    { value: 'under_review', label: 'Under Review', variant: 'warning' },
    { value: 'approved', label: 'Approved', variant: 'success' },
    { value: 'rejected', label: 'Rejected', variant: 'danger' },
    { value: 'request_resubmission', label: 'Request Resubmission', variant: 'purple' },
];

 
const ASSESSMENT_STATUS_VARIANT = {
    sent: 'info', submitted: 'info', expired: 'neutral',
};

 
const REVIEWABLE_STATUSES = ['submitted', 'under_review'];
 
const reviewNotesOf = (r) =>
    r?.org_review_notes || r?.reviewer_notes || r?.review_notes || '';
 
const acceptanceOf = (r) => {
    const raw = r?.org_acceptance_status || r?.acceptance_status || r?.org_acceptance_status_label;
    const v = String(raw || 'pending').toLowerCase().replace(/[\s-]+/g, '_');
    return ['pending', 'accepted', 'rejected', 'follow_up'].includes(v) ? v : 'pending';
};

// 'follow_up' (sent back to the vendor to re-answer) reuses the dashed primary
// box so it reads as "action pending on the vendor" — distinct from a terminal
// reject (red) or an accept (green).
const ACCEPTANCE_TONE = {
    accepted: { box: 'success', label: 'Accepted' },
    rejected: { box: 'danger', label: 'Rejected' },
    follow_up: { box: 'primary', label: 'Follow Up Requested' },
    pending: { box: 'warning', label: 'Pending Review' },
};

const STATUS_CHIP_TONE = {
    warning: { fg: '#C77A15', soft: '#FBF0DD' },
    success: { fg: '#1F9254', soft: '#E4F4EA' },
    danger: { fg: '#D33B3B', soft: '#FBE9E9' },
    purple: { fg: '#5B5BD6', soft: 'rgba(91,91,214,0.12)' },
};

function VendorAssessmentView() {
    const { access_token, assessment_id } = useParams();
    const [loading, setLoading] = useState(true);
    const [assessment, setAssessment] = useState(null);
    const [adminQuestions, setAdminQuestions] = useState([]);
    const [orgQuestions, setOrgQuestions] = useState([]);
    const [assessmentResponses, setAssessmentResponses] = useState([]);
    const [existingResponses, setExistingResponses] = useState([]);
    const [error, setError] = useState('');
    const [responses, setResponses] = useState({});
    const [saving, setSaving] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [isViewMode, setIsViewMode] = useState(false);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [showAssessmentReviewModal, setShowAssessmentReviewModal] = useState(false);
    const [selectedResponse, setSelectedResponse] = useState(null);
    const [reviewNotes, setReviewNotes] = useState('');
    const [reviewerType, setReviewerType] = useState('org');
    const [acceptanceStatus, setAcceptanceStatus] = useState('pending');
    const [reviewing, setReviewing] = useState(false);
    const [assessmentReviewStatus, setAssessmentReviewStatus] = useState('');
    const [assessmentReviewNotes, setAssessmentReviewNotes] = useState('');
    const [assessmentGrade, setAssessmentGrade] = useState('');
    const [acceptanceFilter, setAcceptanceFilter] = useState('All');
    const [updatingAssessmentStatus, setUpdatingAssessmentStatus] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [selectedSubCategory, setSelectedSubCategory] = useState('All');
    const [completionFilter, setCompletionFilter] = useState('All');
    const [showOnlyAiSuggested, setShowOnlyAiSuggested] = useState(false);
    const [focusedQuestionId, setFocusedQuestionId] = useState(null);

    // Bulk file upload states
    const [bulkFiles, setBulkFiles] = useState([]);
    const [uploadingBulkFiles, setUploadingBulkFiles] = useState(false);
    const [bulkUploadMessage, setBulkUploadMessage] = useState('');
    const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);

    // AI Suggestions states
    const [aiSuggestions, setAiSuggestions] = useState([]);
    const [documentUploads, setDocumentUploads] = useState([]);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);
    const [processingStatus, setProcessingStatus] = useState(null);

    // Toast notification state
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    // Previous Assessment modal states
    const [showPreviousAssessmentModal, setShowPreviousAssessmentModal] = useState(false);
    const [previousAssessments, setPreviousAssessments] = useState([]);
    const [loadingPreviousAssessments, setLoadingPreviousAssessments] = useState(false);

    // Per-response document upload states
    const [uploadingResponseId, setUploadingResponseId] = useState(null);
    const [deletingDocumentId, setDeletingDocumentId] = useState(null);
    const [uploadResponseMessage, setUploadResponseMessage] = useState({ id: null, text: '', type: '' });

    // Tutorial state
    const [tutorialStep, setTutorialStep] = useState(null);
    const [cardPos, setCardPos] = useState({ top: 0, left: 0, arrowLeft: '50%' });

    const { confirm, confirmModal } = useConfirm();

    const TUTORIAL_STEPS = [
        { step: 1, target: 'tut-upload-btn', title: 'Upload your documents', desc: 'Start by uploading company documents — policies, certifications, reports. The AI will read them to suggest answers.' },
        { step: 2, target: 'tut-ai-btn', title: 'Get AI-suggested answers', desc: 'Click "View AI Suggestions" to auto-fill your questionnaire based on the uploaded documents.' },
        { step: 3, target: 'tut-questions', title: 'Complete & submit', desc: 'Review the auto-filled answers, complete any remaining questions, then hit Submit.' },
    ];

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 4000);
    };

    const advanceTutorial = () => {
        if (tutorialStep < 3) {
            setTutorialStep(prev => prev + 1);
        } else {
            closeTutorial();
        }
    };

    const closeTutorial = () => {
        setTutorialStep(null);
        if (access_token) localStorage.setItem(`vav_tutorial_${access_token}`, '1');
    };

    // File upload constraints (per API: max 5 files, 28MB total)
    const MAX_FILES = 5;
    const MAX_TOTAL_SIZE = 28 * 1024 * 1024; // 28MB total in bytes
    const MAX_FILE_SIZE = 28 * 1024 * 1024; // 28MB max per validation
    const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt'];

    useEffect(() => {
        if (assessment_id) {
            // View responses mode (authenticated)
            setIsViewMode(true);
            fetchAssessmentResponses();
        } else if (access_token) {
            // Vendor assessment mode (public)
            setIsViewMode(false);
            fetchAssessment();
        }
    }, [access_token, assessment_id]);

    // Show tutorial on first visit (vendor mode only)
    useEffect(() => {
        if (!isViewMode && access_token && !loading) {
            const key = `vav_tutorial_${access_token}`;
            if (!localStorage.getItem(key)) {
                setTutorialStep(1);
            }
        }
    }, [loading]);

    // Disable scroll on step 3, re-enable on dismiss
    useEffect(() => {
        const lockScroll = (e) => e.preventDefault();
        if (tutorialStep === 3) {
            document.body.style.overflow = 'hidden';
            document.documentElement.style.overflow = 'hidden';
            window.addEventListener('wheel', lockScroll, { passive: false });
            window.addEventListener('touchmove', lockScroll, { passive: false });
        } else {
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
            window.removeEventListener('wheel', lockScroll);
            window.removeEventListener('touchmove', lockScroll);
        }
        return () => {
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
            window.removeEventListener('wheel', lockScroll);
            window.removeEventListener('touchmove', lockScroll);
        };
    }, [tutorialStep]);

    // Position tutorial card near the target element
    useEffect(() => {
        if (!tutorialStep) return;
        const step = TUTORIAL_STEPS[tutorialStep - 1];
        const el = document.getElementById(step.target);
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const CARD_WIDTH = 300;
        const arrowDown = tutorialStep === 3;
        const anchorX = rect.left + (tutorialStep === 3 ? 160 : rect.width / 2);
        const anchorY = arrowDown
            ? Math.max(70, rect.top - 205)
            : rect.bottom + 14;
        let left = anchorX;
        left = Math.max(CARD_WIDTH / 2 + 16, Math.min(left, window.innerWidth - CARD_WIDTH / 2 - 16));
        const arrowLeft = `${anchorX - (left - CARD_WIDTH / 2)}px`;
        setCardPos({ top: anchorY, left, arrowLeft, arrowDown });
    }, [tutorialStep]);

    const fetchAssessmentResponses = async (silent = false) => {
        try {
            if (!silent) setLoading(true);

            const [detailsResponse, responsesResponse] = await Promise.all([
                getAssessmentDetailsService(assessment_id),
                getAssessmentResponsesService(assessment_id)
            ]);

            const detailsData = detailsResponse?.statusCode?.response_data?.data || detailsResponse?.data;
            if (detailsData) {
                setAssessment(detailsData);
            }

            const responsesData = responsesResponse?.statusCode?.response_data?.data || responsesResponse?.data;
            if (responsesData) {
                setAssessmentResponses(Array.isArray(responsesData.responses) ? responsesData.responses : []);
            } else {
                setError('Failed to load assessment responses.');
            }
        } catch (err) {
            console.error('Error fetching assessment data:', err);
            setError('Failed to load assessment data. Please try again.');
        } finally {
            if (!silent) setLoading(false);
        }
    };

    const fetchAssessment = async (silent = false) => {
        try {
            if (!silent) setLoading(true);
            const response = await getVendorAssessmentService(access_token);

            const data =
                response?.statusCode?.response_data?.data ||
                response?.data;

            if (!data) {
                const backendMessage = response?.errors?.message || response?.statusCode?.error?.message;
                setError(backendMessage || 'Failed to load assessment.');
                return;
            }

            const {
                assessment,
                admin_questions,
                org_questions,
                ee_questions,
                existing_responses
            } = data;

            const safeAdminQuestions = Array.isArray(admin_questions) ? admin_questions : [];
            const safeOrgQuestions = Array.isArray(org_questions) ? org_questions : [];
            const safeEeQuestions = Array.isArray(ee_questions) ? ee_questions : [];
            const safeExistingResponses = Array.isArray(existing_responses) ? existing_responses : [];

            const prefilledResponses = {};

            const typeOf = (q) => String(q?.response_type || q?.response_type_display || '').toLowerCase();
            const adminTypeById = {};
            [...safeAdminQuestions, ...safeEeQuestions].forEach(q => { adminTypeById[q.id] = typeOf(q); });
            const orgTypeById = {};
            safeOrgQuestions.forEach(q => { orgTypeById[q.id] = typeOf(q); });
            const isMulti = (t) => t === 'multi_select' || t === 'multi-select';
            const parseValue = (text, type) => {
                if (text == null) return text;
                if (isMulti(type)) {
                    return String(text).split(',').map(v => v.trim()).filter(Boolean);
                }
                return text;
            };

            safeExistingResponses.forEach(res => {
                if (res.vendor_question) {
                    prefilledResponses[`admin_${res.vendor_question}`] =
                        parseValue(res.response_text, adminTypeById[res.vendor_question]);
                    if (res.explanation) {
                        prefilledResponses[`admin_text_${res.vendor_question}`] = res.explanation;
                    }
                }

                if (res.org_vendor_question) {
                    prefilledResponses[`org_${res.org_vendor_question}`] =
                        parseValue(res.response_text, orgTypeById[res.org_vendor_question]);
                    if (res.explanation) {
                        prefilledResponses[`org_text_${res.org_vendor_question}`] = res.explanation;
                    }
                }
            });

            setExistingResponses(safeExistingResponses);
            setResponses(prefilledResponses);
            setAssessment(assessment || {});
            setAdminQuestions([...safeAdminQuestions, ...safeEeQuestions]);
            setOrgQuestions(safeOrgQuestions);

        } catch (err) {
            console.error(err);
            if (!silent) {
                const backendMessage = err?.response?.data?.error?.message;
                setError(backendMessage || 'Failed to load assessment.');
            }
        } finally {
            if (!silent) setLoading(false);
        }
    };

    // Evidence is served behind an authenticated TPRM endpoint, so it must be
    // fetched as a blob through ApiIntercepter (which attaches the auth headers)
    // — a raw window.open/<a href> would be unauthenticated. The server returns
    // an application/json blob on error, which we surface as a toast.
    const blobFromRes = async (res) => {
        const blob = res?.statusCode;
        if (!blob) throw new Error('Empty response from server');
        if (blob.type === 'application/json') {
            const data = JSON.parse(await blob.text());
            throw new Error(data?.error?.message || 'Unable to load evidence.');
        }
        return blob;
    };

    const openBlob = (blob) => {
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank', 'noopener,noreferrer');
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    };

    const downloadBlob = (blob, filename) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || 'evidence';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    };

    // Reviewer side (authenticated): assessment_id / response.id / document_id
    const handleViewEvidence = async (response, doc) => {
        try {
            openBlob(await blobFromRes(await getAssessmentResponseDocumentService(
                assessment_id, response?.id, doc?.document_id, false
            )));
        } catch (e) {
            showToast(e?.message || 'Failed to open evidence.', 'error');
        }
    };

    const handleDownloadEvidence = async (response, doc) => {
        try {
            const blob = await blobFromRes(await getAssessmentResponseDocumentService(
                assessment_id, response?.id, doc?.document_id, true
            ));
            downloadBlob(blob, doc?.filename);
        } catch (e) {
            showToast(e?.message || 'Failed to download evidence.', 'error');
        }
    };

    // Vendor portal (public email link): access_token / response.id / document_id
    const handleViewVendorEvidence = async (res, doc) => {
        try {
            openBlob(await blobFromRes(await getVendorResponseDocumentService(
                access_token, res?.id, doc?.document_id, false
            )));
        } catch (e) {
            showToast(e?.message || 'Failed to open evidence.', 'error');
        }
    };

    const handleDownloadVendorEvidence = async (res, doc) => {
        try {
            const blob = await blobFromRes(await getVendorResponseDocumentService(
                access_token, res?.id, doc?.document_id, true
            ));
            downloadBlob(blob, doc?.filename);
        } catch (e) {
            showToast(e?.message || 'Failed to download evidence.', 'error');
        }
    };

    const handleReviewResponse = (response) => {
        setSelectedResponse(response);
        setReviewNotes(reviewNotesOf(response));
        setAcceptanceStatus(acceptanceOf(response));
        setShowReviewModal(true);
    };

    const closeReviewModal = () => {
        setShowReviewModal(false);
        setSelectedResponse(null);
        setReviewNotes('');
        setReviewerType('org');
        setAcceptanceStatus('pending');
    };

    const handleSubmitReview = async () => {
        if (!selectedResponse) return;
        if (!REVIEWABLE_STATUSES.includes(assessment?.status)) {
            showToast('Assessment must be submitted to review responses.', 'warning');
            return;
        }
        if (acceptanceStatus === 'follow_up' && !reviewNotes.trim()) {
            showToast('Add review notes explaining what the vendor needs to fix before sending it back.', 'warning');
            return;
        }

        setReviewing(true);
        try {
            const response = await reviewResponseService(selectedResponse.id, {
                reviewer_type: reviewerType,
                acceptance_status: acceptanceStatus,
                review_notes: reviewNotes
            });

            const successMessage =
                response?.statusCode?.response_data?.message ||
                response?.statusCode?.message ||
                response?.data?.message;

            if (successMessage) {
                fetchAssessmentResponses(true);
                closeReviewModal();
                showToast(successMessage || 'Response reviewed successfully!', 'success');
            } else {
                showToast('Failed to review response. Please try again.', 'error');
            }
        } catch (error) {
            console.error('Error reviewing response:', error);
            showToast('Failed to review response. Please try again.', 'error');
        } finally {
            setReviewing(false);
        }
    };

    const closeAssessmentReviewModal = () => {
        setShowAssessmentReviewModal(false);
        setAssessmentReviewStatus('');
        setAssessmentReviewNotes('');
        setAssessmentGrade('');
    };

    const handleUpdateAssessmentStatus = async () => {
        if (!assessment_id) return;

        setUpdatingAssessmentStatus(true);
        try {
            const params = {
                action: assessmentReviewStatus,
                reason: assessmentReviewNotes
            };
            if (['approved', 'rejected'].includes(assessmentReviewStatus) && assessmentGrade) {
                params.grade = assessmentGrade;
            }
            const response = await updateAssessmentReviewStatusService(assessment_id, params);

            const successMessage =
                response?.statusCode?.response_data?.message ||
                response?.statusCode?.message ||
                response?.data?.message;

            if (successMessage) {
                fetchAssessmentResponses(true);
                closeAssessmentReviewModal();
                showToast(successMessage || 'Assessment review status updated successfully!', 'success');
            } else {
                showToast('Failed to update assessment status. Please try again.', 'error');
            }
        } catch (error) {
            console.error('Error updating assessment status:', error);
            showToast('Failed to update assessment status. Please try again.', 'error');
        } finally {
            setUpdatingAssessmentStatus(false);
        }
    };

    // Bulk file upload functions
    const validateFile = (file) => {
        const errors = [];
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

        if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
            errors.push(`Invalid format: ${file.name}. Allowed: PDF, DOC, DOCX, XLS, XLSX, CSV`);
        }

        if (file.size > MAX_FILE_SIZE) {
            errors.push(`File too large: ${file.name}. Max size: 28MB`);
        }

        return errors;
    };

    const handleBulkFileSelect = (e) => {
        const selectedFiles = Array.from(e.target.files);
        const allErrors = [];
        const validFiles = [];

        if (bulkFiles.length + selectedFiles.length > MAX_FILES) {
            showToast(`Maximum ${MAX_FILES} files allowed. You already have ${bulkFiles.length} files selected.`, 'error');
            e.target.value = '';
            return;
        }

        const currentTotalSize = bulkFiles.reduce((sum, f) => sum + f.size, 0);
        const selectedTotalSize = selectedFiles.reduce((sum, f) => sum + f.size, 0);
        if (currentTotalSize + selectedTotalSize > MAX_TOTAL_SIZE) {
            showToast(`Total file size across all files cannot exceed 28MB. Current: ${formatFileSize(currentTotalSize)}, New: ${formatFileSize(selectedTotalSize)}`, 'error');
            e.target.value = '';
            return;
        }

        selectedFiles.forEach(file => {
            const errors = validateFile(file);
            if (errors.length > 0) {
                allErrors.push(...errors);
            } else {
                const isDuplicate = bulkFiles.some(f => f.name === file.name && f.size === file.size);
                if (!isDuplicate) {
                    validFiles.push(file);
                } else {
                    allErrors.push(`Duplicate file: ${file.name}`);
                }
            }
        });

        if (allErrors.length > 0) {
            showToast('Some files were not added: ' + allErrors.join(', '), 'error');
        }

        if (validFiles.length > 0) {
            setBulkFiles(prev => [...prev, ...validFiles]);
        }

        e.target.value = '';
    };

    const removeBulkFile = async (index) => {
        const file = bulkFiles[index];
        if (!(await confirm({
            title: 'Remove file',
            message: `Remove "${file?.name}" from the upload list?`,
            confirmLabel: 'Remove',
            danger: true,
        }))) return;
        setBulkFiles(prev => prev.filter((_, i) => i !== index));
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleBulkUpload = async () => {
        if (bulkFiles.length === 0) {
            showToast('Please select at least one file to upload.', 'error');
            return;
        }

        if (!access_token) {
            showToast('Access token not found. Please try again.', 'error');
            return;
        }

        const totalSize = bulkFiles.reduce((sum, file) => sum + file.size, 0);
        if (totalSize > MAX_TOTAL_SIZE) {
            showToast(`Total file size exceeds 28MB. Current total: ${formatFileSize(totalSize)}`, 'error');
            return;
        }

        setUploadingBulkFiles(true);
        setBulkUploadMessage('');

        try {
            const response = await uploadVendorDocumentsService(access_token, bulkFiles);

            const successMessage =
                response?.data?.message ||
                response?.statusCode?.response_data?.message ||
                response?.statusCode?.message ||
                'Documents uploaded successfully. AI processing started.';

            const uploadData = response?.data?.data || response?.statusCode?.response_data?.data;

            if (uploadData) {
                setProcessingStatus(uploadData.processing_status);
            }

            setBulkUploadMessage(successMessage);
            setBulkFiles([]);
            setShowBulkUploadModal(false);
            showToast(successMessage, 'success');

            fetchQuestionSuggestions();
        } catch (error) {
            console.error('Error uploading documents:', error);
            const errorMessage =
                error?.response?.data?.error_msg ||
                error?.response?.data?.message ||
                'Failed to upload files. Please try again.';
            setBulkUploadMessage(errorMessage);
            showToast(errorMessage, 'error');
        } finally {
            setUploadingBulkFiles(false);
        }
    };

    // Fetch AI-generated question suggestions
    const fetchQuestionSuggestions = async (isManualClick = false) => {
        if (!access_token) return;

        setLoadingSuggestions(true);
        try {
            const response = await getVendorQuestionSuggestionsService(access_token);
            const data = response?.statusCode?.response_data?.data || response?.response_data?.data || response?.data;
            if (data) {
                const suggestions = data.suggestions || [];
                const uploads = data.document_uploads || [];

                setAiSuggestions(suggestions);
                setDocumentUploads(uploads);

                if (isManualClick && uploads.length === 0) {
                    setTutorialStep(1);
                }

                const processing = uploads.some(u => u.status === 'processing' || u.status === 'pending');
                setProcessingStatus(processing ? 'processing' : 'completed');
            }
        } catch (error) {
            console.error('Error fetching suggestions:', error);
            setProcessingStatus('failed');
        } finally {
            setLoadingSuggestions(false);
        }
    };

    // Polling effect: call API every 7 seconds while processing
    useEffect(() => {
        let intervalId = null;

        if (processingStatus === 'processing') {
            intervalId = setInterval(() => {
                fetchQuestionSuggestions();
            }, 7000);
        }

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [processingStatus, access_token]);

    // Apply AI suggestion to a question
    const applySuggestion = (questionId, questionType, suggestionText, responseType = '') => {
        let key = `${questionType}_${questionId}`;

        if (['yes_no', 'yes/no', 'multi_select', 'multi-select'].includes((responseType || '').toLowerCase())) {
            key = `${questionType}_text_${questionId}`;
        }

        setResponses(prev => ({
            ...prev,
            [key]: suggestionText
        }));
    };

    const formatResponsesForAPI = () => {
        const apiResponses = [];

        adminQuestions.forEach(question => {
            const key = `admin_${question.id}`;
            const textKey = `admin_text_${question.id}`;
            const fileKey = `admin_file_${question.id}`;
            const value = responses[key];
            const textValue = responses[textKey];
            const fileValue = responses[fileKey];

            if (value !== undefined && value !== null && value !== '' || textValue || fileValue) {
                let responseText = '';

                if (Array.isArray(value)) {
                    responseText = value.join(', ');
                } else if (typeof value === 'object' && value instanceof File) {
                    responseText = value.name;
                } else if (value) {
                    responseText = String(value);
                }

                const responseObj = {
                    vendor_question: question.id,
                    response_text: responseText
                };

                if (textValue) {
                    responseObj.explanation = textValue;
                }
                if (fileValue && fileValue instanceof File) {
                    responseObj.response_file = fileValue;
                }

                apiResponses.push(responseObj);
            }
        });

        orgQuestions.forEach(question => {
            const key = `org_${question.id}`;
            const textKey = `org_text_${question.id}`;
            const fileKey = `org_file_${question.id}`;
            const value = responses[key];
            const textValue = responses[textKey];
            const fileValue = responses[fileKey];

            if (value !== undefined && value !== null && value !== '' || textValue || fileValue) {
                let responseText = '';

                if (Array.isArray(value)) {
                    responseText = value.join(', ');
                } else if (typeof value === 'object' && value instanceof File) {
                    responseText = value.name;
                } else if (value) {
                    responseText = String(value);
                }

                const responseObj = {
                    org_vendor_question: question.id,
                    response_text: responseText
                };

                if (textValue) {
                    responseObj.explanation = textValue;
                }
                if (fileValue && fileValue instanceof File) {
                    responseObj.response_file = fileValue;
                }

                apiResponses.push(responseObj);
            }
        });

        return apiResponses;
    };

    const handleAutoSave = async () => {
        const apiResponses = formatResponsesForAPI();

        if (apiResponses.length === 0) {
            return;
        }

        setSaving(true);

        try {
            await submitVendorAssessmentService(access_token, {
                responses: apiResponses,
                submit: false
            });

            showToast('Progress saved', 'success');
        } catch (error) {
            console.error('Error auto-saving:', error);
            showToast('Failed to save', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleFinalSubmit = async () => {
        const apiResponses = formatResponsesForAPI();

        if (apiResponses.length === 0) {
            showToast('Please answer at least one question before submitting.', 'error');
            return;
        }

        const missingExplanations = [];

        adminQuestions.forEach(q => {
            const textKey = `admin_text_${q.id}`;
            const textValue = responses[textKey];
            if (!textValue || textValue.trim() === '') {
                missingExplanations.push(q);
            }
        });

        orgQuestions.forEach(q => {
            const textKey = `org_text_${q.id}`;
            const textValue = responses[textKey];
            if (!textValue || textValue.trim() === '') {
                missingExplanations.push(q);
            }
        });

        if (missingExplanations.length > 0) {
            showToast(`Please fill in the explanation for all questions. ${missingExplanations.length} explanation(s) are missing.`, 'error');
            return;
        }

        const unansweredRequired = [];

        adminQuestions.forEach(q => {
            if (q.is_required) {
                const key = `admin_${q.id}`;
                const textKey = `admin_text_${q.id}`;
                const value = responses[key];
                const textValue = responses[textKey];
                if (!value || value === '' || (Array.isArray(value) && value.length === 0) || !textValue || textValue.trim() === '') {
                    unansweredRequired.push(q);
                }
            }
        });

        orgQuestions.forEach(q => {
            if (q.is_required) {
                const key = `org_${q.id}`;
                const textKey = `org_text_${q.id}`;
                const value = responses[key];
                const textValue = responses[textKey];
                if (!value || value === '' || (Array.isArray(value) && value.length === 0) || !textValue || textValue.trim() === '') {
                    unansweredRequired.push(q);
                }
            }
        });

        if (unansweredRequired.length > 0) {
            showToast(`Please answer all required questions. ${unansweredRequired.length} required question(s) remaining.`, 'error');
            return;
        }

        setSubmitting(true);

        try {
            const response = await submitVendorAssessmentService(access_token, {
                responses: apiResponses,
                submit: true
            });

            // The API can reject the submission with an HTTP 200 + a business-level
            // error body (e.g. token expired between page load and submit) — check
            // for that before assuming success, otherwise the vendor sees a false
            // "submitted successfully" toast while the org never received anything.
            const backendErrorMessage = response?.errors?.message || response?.statusCode?.error?.message;
            if (backendErrorMessage) {
                showToast(backendErrorMessage, 'error');
                return;
            }

            const successMessage =
                response?.statusCode?.response_data?.message ||
                response?.statusCode?.message ||
                response?.data?.message ||
                'Assessment submitted successfully!';

            showToast(successMessage, 'success');

            fetchAssessment();
        } catch (error) {
            console.error('Error submitting assessment:', error);
            showToast('Failed to submit assessment. Please try again.', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    // Auto-save + refresh after response change (for radio/yes-no)
    const handleResponseChangeAndSave = async (questionId, value, questionType = 'admin') => {
        setResponses(prev => ({
            ...prev,
            [`${questionType}_${questionId}`]: value
        }));

        const responseObj = questionType === 'admin'
            ? { vendor_question: questionId, response_text: String(value) }
            : { org_vendor_question: questionId, response_text: String(value) };

        const textKey = `${questionType}_text_${questionId}`;
        if (responses[textKey]) {
            responseObj.explanation = responses[textKey];
        }

        try {
            await submitVendorAssessmentService(access_token, {
                responses: [responseObj],
                submit: false
            });
            // Refetch so existingResponses picks up the id the server just
            // assigned. The per-question 'Upload Supporting Document' button
            // is gated on that id (hasResponseId), so without this it stayed
            // disabled until the explanation textarea happened to blur.
            await fetchAssessment(true);
            showToast('Progress saved', 'success');
        } catch (error) {
            console.error('Error saving response:', error);
            showToast('Failed to save', 'error');
        }
    };

    // Auto-save + refresh after multi-select change
    const handleMultiSelectChangeAndSave = async (questionId, option, questionType = 'admin') => {
        const key = `${questionType}_${questionId}`;
        // Coerce to an array before toggling — a single-option answer may arrive
        // as a bare string, and spreading a string would split it into chars.
        const raw = responses[key];
        const currentValues = Array.isArray(raw)
            ? raw
            : (raw ? String(raw).split(',').map(v => v.trim()).filter(Boolean) : []);

        const newValues = currentValues.includes(option)
            ? currentValues.filter(v => v !== option)
            : [...currentValues, option];

        setResponses(prev => ({
            ...prev,
            [key]: newValues
        }));

        const responseText = newValues.join(', ');
        const responseObj = questionType === 'admin'
            ? { vendor_question: questionId, response_text: responseText }
            : { org_vendor_question: questionId, response_text: responseText };

        const textKey = `${questionType}_text_${questionId}`;
        if (responses[textKey]) {
            responseObj.explanation = responses[textKey];
        }

        try {
            await submitVendorAssessmentService(access_token, {
                responses: [responseObj],
                submit: false
            });
            // Refetch so existingResponses picks up the id the server just
            // assigned. The per-question 'Upload Supporting Document' button
            // is gated on that id (hasResponseId), so without this it stayed
            // disabled until the explanation textarea happened to blur.
            await fetchAssessment(true);
            showToast('Progress saved', 'success');
        } catch (error) {
            console.error('Error saving response:', error);
            showToast('Failed to save', 'error');
        }
    };

    // Upload document to a specific response
    const handleResponseDocumentUpload = async (responseId, file) => {
        try {
            setUploadingResponseId(responseId);
            setUploadResponseMessage({ id: null, text: '', type: '' });
            await uploadVendorResponseDocumentsService(access_token, responseId, [file]);
            setUploadResponseMessage({ id: responseId, text: 'Document uploaded successfully!', type: 'success' });
            await fetchAssessment(true);
            setTimeout(() => setUploadResponseMessage({ id: null, text: '', type: '' }), 3000);
        } catch (error) {
            console.error('Error uploading document:', error);
            setUploadResponseMessage({ id: responseId, text: 'Failed to upload document.', type: 'error' });
            setTimeout(() => setUploadResponseMessage({ id: null, text: '', type: '' }), 3000);
        } finally {
            setUploadingResponseId(null);
        }
    };

    // Delete document from a specific response
    const handleDeleteDocument = async (responseId, documentId) => {
        if (!(await confirm({
            title: 'Delete document',
            message: 'Delete this uploaded document? This cannot be undone.',
            confirmLabel: 'Delete',
            danger: true,
        }))) return;

        try {
            setDeletingDocumentId(documentId);
            setUploadResponseMessage({ id: null, text: '', type: '' });
            await deleteVendorResponseDocumentsService(access_token, responseId, [documentId]);
            setUploadResponseMessage({ id: responseId, text: 'Document deleted successfully!', type: 'success' });
            await fetchAssessment(true);
            setTimeout(() => setUploadResponseMessage({ id: null, text: '', type: '' }), 3000);
        } catch (error) {
            console.error('Error deleting document:', error);
            setUploadResponseMessage({ id: responseId, text: 'Failed to delete document.', type: 'error' });
            setTimeout(() => setUploadResponseMessage({ id: null, text: '', type: '' }), 3000);
        } finally {
            setDeletingDocumentId(null);
        }
    };

    // Helper function to get review info for a question
    const getReviewInfoForQuestion = (questionId, questionType = 'admin') => {
        if (!existingResponses || existingResponses.length === 0) return null;

        let response = null;
        if (questionType === 'admin') {
            response = existingResponses.find(res => res.vendor_question === questionId);
        } else {
            response = existingResponses.find(res => res.org_vendor_question === questionId);
        }

        if (response && response.is_reviewed) {
            return {
                reviewed_by_name: response.reviewed_by_name,
                reviewer_notes: reviewNotesOf(response),
                is_reviewed: response.is_reviewed,
                acceptance: acceptanceOf(response)
            };
        }
        return null;
    };

    // Statuses where the vendor is doing the first-pass fill — the whole form is open.
    const isFirstPassEditable = ['draft', 'in_progress', 'sent'].includes(assessment?.status);
    // Terminal states — the assessment is closed, nothing can be edited.
    const isTerminalStatus = ['approved', 'expired', 'rejected'].includes(assessment?.status);

    // A response is "flagged" when the reviewer marked it follow_up or rejected — those
    // are the only questions the vendor is asked to re-answer.
    const isResponseFlagged = (res) => {
        const status = acceptanceOf(res);
        return status === 'follow_up' || status === 'rejected';
    };

    // A single question is editable when the vendor is doing a first-pass fill, OR the
    // assessment is back with the vendor for fixes (any non-first-pass, non-terminal
    // state — submitted / under_review / request_resubmission) AND this specific
    // question was flagged. Reviewers flag questions individually, so the assessment
    // status often stays `submitted`/`under_review` even though the vendor got the
    // email to re-answer — the per-response flag, not the assessment status, is the
    // real signal. Everything else stays locked so an accepted answer can't change.
    const isQuestionEditable = (question, questionType = 'admin') => {
        if (isFirstPassEditable) return true;
        if (isTerminalStatus) return false;
        const res = existingResponses.find(r =>
            questionType === 'admin' ? r.vendor_question === question.id : r.org_vendor_question === question.id
        );
        return isResponseFlagged(res);
    };

    // The toolbar (Submit / Upload / AI Suggestions) is live during a first-pass fill,
    // or when there is at least one flagged question the vendor can re-answer.
    const hasEditableFlaggedQuestion = !isFirstPassEditable && !isTerminalStatus &&
        existingResponses.some(isResponseFlagged);
    const isFormDisabled = !isFirstPassEditable && !hasEditableFlaggedQuestion;

    const renderInputField = (question, questionType = 'admin') => {
        const questionKey = `${questionType}_${question.id}`;
        const responseType = question.response_type || question.response_type_display || '';
        const currentValue = responses[questionKey];
        const textKey = `${questionType}_text_${question.id}`;
        // Per-question lock: during a resubmission only the reviewer-flagged
        // questions open up; otherwise this mirrors the whole-form lock.
        const questionDisabled = !isQuestionEditable(question, questionType);

        const renderPrimaryInput = () => {
            switch (responseType.toLowerCase()) {
                case 'yes_no':
                case 'yes/no':
                    return (
                        <div className="va-radio-row" role="group">
                            <label className="va-radio-option">
                                <input
                                    type="radio"
                                    name={questionKey}
                                    checked={currentValue === 'yes'}
                                    onChange={() => handleResponseChangeAndSave(question.id, 'yes', questionType)}
                                    onFocus={() => setFocusedQuestionId(question.id)}
                                    onBlur={() => setFocusedQuestionId(null)}
                                    disabled={questionDisabled}
                                />
                                Yes
                            </label>
                            <label className="va-radio-option">
                                <input
                                    type="radio"
                                    name={questionKey}
                                    checked={currentValue === 'no'}
                                    onChange={() => handleResponseChangeAndSave(question.id, 'no', questionType)}
                                    onFocus={() => setFocusedQuestionId(question.id)}
                                    onBlur={() => setFocusedQuestionId(null)}
                                    disabled={questionDisabled}
                                />
                                No
                            </label>
                        </div>
                    );

                case 'multi_select':
                case 'multi-select': {
                    const options = question.options || question.multi_select_options || [];
                    // Normalise to an array so a single-option answer stored as a
                    // string ("AWS") still checks the right box (and never
                    // substring-matches, e.g. "AWS" vs "A").
                    const selectedValues = Array.isArray(currentValue)
                        ? currentValue
                        : (currentValue ? String(currentValue).split(',').map(v => v.trim()).filter(Boolean) : []);
                    return (
                        <div>
                            <small className="va-question-hint d-block mb-2">Select all that apply:</small>
                            {options.map((option, idx) => (
                                <label key={idx} className="va-checkbox-option">
                                    <input
                                        type="checkbox"
                                        checked={selectedValues.includes(option)}
                                        onChange={() => handleMultiSelectChangeAndSave(question.id, option, questionType)}
                                        onFocus={() => setFocusedQuestionId(question.id)}
                                        onBlur={() => setFocusedQuestionId(null)}
                                        disabled={questionDisabled}
                                    />
                                    {option}
                                </label>
                            ))}
                        </div>
                    );
                }

                default:
                    return null;
            }
        };

        return (
            <div>
                {renderPrimaryInput()}

                <div className="mt-3">
                    <small className="va-field-label d-block mb-2">Explanation *</small>
                    <textarea
                        className="va-textarea"
                        rows="3"
                        required
                        placeholder="Enter your explanation or details here..."
                        value={responses[textKey] || ''}
                        onChange={(e) => {
                            setResponses(prev => ({
                                ...prev,
                                [textKey]: e.target.value
                            }));
                        }}
                        onFocus={() => setFocusedQuestionId(question.id)}
                        onBlur={async () => {
                            setFocusedQuestionId(null);
                            if (responses[textKey]?.trim()) {
                                await handleAutoSave();
                                await fetchAssessment(true);
                            }
                        }}
                        disabled={questionDisabled}
                    />
                </div>

                {(() => {
                    const existingRes = existingResponses.find(res =>
                        questionType === 'admin' ? res.vendor_question === question.id : res.org_vendor_question === question.id
                    );

                    const hasResponseId = existingRes && existingRes.id;
                    const isUploading = hasResponseId ? uploadingResponseId === existingRes.id : false;
                    const msg = hasResponseId && uploadResponseMessage.id === existingRes.id ? uploadResponseMessage : null;
                    const isDisabled = questionDisabled || isUploading || !hasResponseId;
                    const uploadInputId = `response-file-${hasResponseId ? existingRes.id : question.id}`;

                    return (
                        <div className="mt-3">
                            <div className="d-flex gap-3 align-items-center flex-wrap">
                                <small className="va-question-hint mb-0">Upload Supporting Document:</small>
                                <input
                                    type="file"
                                    id={uploadInputId}
                                    style={{ display: 'none' }}
                                    onChange={(e) => {
                                        if (hasResponseId && e.target.files[0]) {
                                            handleResponseDocumentUpload(existingRes.id, e.target.files[0]);
                                        }
                                    }}
                                    disabled={isDisabled}
                                />
                                <span title={!hasResponseId ? 'Document upload will remain disabled until an answer is provided.' : undefined}>
                                    <label htmlFor={uploadInputId} style={{ display: 'inline-block' }}>
                                        <CommonButton
                                            type="button"
                                            text={isUploading ? 'Uploading...' : 'Upload'}
                                            variant="secondary"
                                            disable={isDisabled}
                                            onClick={() => document.getElementById(uploadInputId)?.click()}
                                        />
                                    </label>
                                </span>
                                {hasResponseId && existingRes.documents && existingRes.documents.length > 0 && (
                                    <StatusBadge variant="success">{existingRes.documents.length} document(s) uploaded</StatusBadge>
                                )}
                            </div>

                            {hasResponseId && existingRes.documents && existingRes.documents.length > 0 && (
                                <div className="mt-2">
                                    {existingRes.documents.map((doc, dIdx) => (
                                        <div key={dIdx} className="d-flex align-items-center gap-2 mb-1">
                                            <a
                                                href="#"
                                                onClick={(e) => { e.preventDefault(); handleViewVendorEvidence(existingRes, doc); }}
                                                className="va-doc-link"
                                            >
                                                {doc.filename}
                                            </a>
                                            <span className="va-doc-meta">
                                                <span>({(doc.size / 1024).toFixed(1)} KB)</span>
                                                <span>{new Date(doc.uploaded_at).toLocaleDateString()}</span>
                                            </span>
                                            <CommonActionMenu
                                                iconOnly
                                                actions={[
                                                    { label: 'View', title: 'View', icon: 'preview', onClick: () => handleViewVendorEvidence(existingRes, doc) },
                                                    { label: 'Download', title: 'Download', icon: 'download', onClick: () => handleDownloadVendorEvidence(existingRes, doc) },
                                                ]}
                                            />
                                            {!questionDisabled && (
                                                <CommonButton
                                                    type="button"
                                                    text={deletingDocumentId === doc.document_id ? 'Removing...' : 'Remove'}
                                                    variant="error"
                                                    className="tpcrm-row-btn"
                                                    disable={deletingDocumentId === doc.document_id || uploadingResponseId === existingRes.id}
                                                    onClick={() => handleDeleteDocument(existingRes.id, doc.document_id)}
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                            {msg && (
                                <StatusBadge variant={msg.type === 'success' ? 'success' : 'danger'} className="mt-1">{msg.text}</StatusBadge>
                            )}
                        </div>
                    );
                })()}
            </div>
        );
    };

    if (loading) {
        return <FullPageLoader loading={loading} />;
    }

    if (error) {
        return (
            <div className="va-page d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
                <div className="va-card text-center" style={{ maxWidth: '500px' }}>
                    <h3 style={{ color: 'var(--color-danger)' }}>Assessment Not Found</h3>
                    <p style={{ color: 'var(--color-text-secondary)' }} className="mb-0">{error}</p>
                </div>
            </div>
        );
    }

    // View Responses Mode (Authenticated)
    if (isViewMode) {
        const filteredResponses = assessmentResponses.filter(r => {
            const categoryMatch = selectedCategory === 'All' || r.question_category === selectedCategory;
            const subCategoryMatch = selectedSubCategory === 'All' || (r.sub_category || r.question_sub_category) === selectedSubCategory;
            const acceptanceMatch = acceptanceFilter === 'All' || (r.org_acceptance_status_label || 'Pending') === acceptanceFilter;

            const isAnswered = (r.response_text && r.response_text.trim() !== '' && (!Array.isArray(r.response_text) || r.response_text.length > 0)) || (r.explanation && r.explanation.trim() !== '');
            const completionMatch = completionFilter === 'All' || (completionFilter === 'Answered' ? isAnswered : !isAnswered);

            return categoryMatch && subCategoryMatch && acceptanceMatch && completionMatch;
        });

        const categoryOptions = toOptions(['All', ...new Set(assessmentResponses.map(r => r.question_category).filter(Boolean))], 'Category');
        const subCategoryOptions = toOptions(['All', ...new Set(assessmentResponses
            .filter(r => selectedCategory === 'All' || r.question_category === selectedCategory)
            .map(r => r.sub_category || r.question_sub_category)
            .filter(Boolean))], 'Sub-category');

        const canUpdateStatus = REVIEWABLE_STATUSES.includes(assessment?.status);
        const updateStatusVariant = assessmentReviewStatus === 'approved' ? 'success' : assessmentReviewStatus === 'rejected' ? 'error' : 'primary';

        const resetFilters = () => {
            setSelectedCategory('All');
            setSelectedSubCategory('All');
            setAcceptanceFilter('All');
            setCompletionFilter('All');
        };

        return (
            <div className="va-page">
                <div className="va-container">
                    {confirmModal}
                    {/* Assessment Details Card */}
                    {assessment && (
                        <div className="va-card">
                            <div className="va-card-header">
                                <div>
                                    <h2>{assessment?.vendor_name || 'Assessment Details'}</h2>
                                    <p className="va-field-value mb-0">{`Assessment #${assessment.id || assessment_id}`}</p>
                                    {assessment?.vendor_email && (
                                        <p className="va-question-hint mb-0">{`Vendor Email: ${assessment.vendor_email}`}</p>
                                    )}
                                </div>
                                <CommonButton
                                    type="button"
                                    text="Assessment History"
                                    variant="primary"
                                    onClick={async () => {
                                        setShowPreviousAssessmentModal(true);
                                        setLoadingPreviousAssessments(true);
                                        try {
                                            let query = `?vendor_name=${assessment?.vendor_name}`;
                                            const response = await getTprmAssessmentsService(query);
                                            const data = response?.statusCode?.response_data?.data?.assessments || response?.data?.response_data?.data?.assessments || [];
                                            setPreviousAssessments(Array.isArray(data) ? data : []);
                                        } catch (err) {
                                            console.error('Error fetching previous assessments:', err);
                                            setPreviousAssessments([]);
                                        } finally {
                                            setLoadingPreviousAssessments(false);
                                        }
                                    }}
                                />
                            </div>

                            {/* At-a-glance summary — the 4 facts that matter most, pulled out
                                of the metadata grid so they read instantly instead of being
                                just another label/value pair among a dozen others. */}
                            <div className="va-summary-strip">
                                <div className="va-summary-stat">
                                    <span className="va-field-label">Status</span>
                                    <StatusBadge variant={ASSESSMENT_STATUS_VARIANT[assessment?.status]} status={formatText(assessment?.status) || 'N/A'} size="md" />
                                </div>
                                <div className="va-summary-stat">
                                    <span className="va-field-label">Grade</span>
                                    <span className="tpcrm-grade-text" style={{ color: gradeTone(assessment?.grade) }}>{assessment?.grade || '-'}</span>
                                </div>
                                <div className="va-summary-stat">
                                    <span className="va-field-label">Questionnaire</span>
                                    <span className="va-field-value">{assessment?.questionnaire_info?.questionnaire_name || 'N/A'}</span>
                                </div>
                                <div className="va-summary-stat">
                                    <span className="va-field-label">Expires</span>
                                    <span className="va-field-value">
                                        {assessment?.expires_at
                                            ? new Date(assessment.expires_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                                            : 'N/A'}
                                    </span>
                                </div>
                            </div>

                            <div className="va-grid">
                                <div className="va-field">
                                    <span className="va-field-label">Created At</span>
                                    <span className="va-field-value">
                                        {assessment?.created_at
                                            ? new Date(assessment.created_at).toLocaleString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                                            : 'N/A'}
                                    </span>
                                </div>
                                <div className="va-field">
                                    <span className="va-field-label">Sent At</span>
                                    <span className="va-field-value">
                                        {assessment?.sent_at
                                            ? new Date(assessment.sent_at).toLocaleString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                                            : 'N/A'}
                                    </span>
                                </div>
                                {assessment?.submitted_at && (
                                    <div className="va-field">
                                        <span className="va-field-label">Submitted At</span>
                                        <span className="va-field-value">
                                            {new Date(assessment.submitted_at).toLocaleString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                    </div>
                                )}
                                <div className="va-field"><span className="va-field-label">Expiry Days</span><span className="va-field-value">{assessment?.expiry_days || 'N/A'} days</span></div>
                            </div>
                        </div>
                    )}

                    {/* Responses Card */}
                    <div className="va-card">
                        <div className="va-card-header">
                            <h3>Assessment Responses</h3>
                            {canUpdateStatus ? (
                                <CommonButton type="button" text="Update Assessment Status" variant="primary" onClick={() => { setAssessmentReviewStatus(STATUS_PICKER_OPTIONS.some(o => o.value === assessment?.status) ? assessment.status : ''); setAssessmentReviewNotes(assessment?.review_notes || ''); setAssessmentGrade(assessment?.grade || ''); setShowAssessmentReviewModal(true); }} />
                            ) : (
                                <span title={assessment?.status === 'submitted' || assessment?.status === 'under_review' ? undefined : `Status can only be updated once the vendor has submitted. Current status: ${formatText(assessment?.status) || 'unknown'}.`}>
                                    <CommonButton type="button" text="Update Assessment Status" variant="secondary" disable />
                                </span>
                            )}
                        </div>

                        <div className="va-filters">
                            <CommonDropdown ariaLabel="Acceptance status" value={acceptanceFilter} onChange={setAcceptanceFilter} options={ACCEPTANCE_STATUS_OPTIONS} minWidth={140} />
                            <CommonDropdown ariaLabel="Category" value={selectedCategory} onChange={(v) => { setSelectedCategory(v); setSelectedSubCategory('All'); }} options={categoryOptions} minWidth={150} />
                            <CommonDropdown ariaLabel="Sub-category" value={selectedSubCategory} onChange={setSelectedSubCategory} options={subCategoryOptions} minWidth={150} />
                            <CommonDropdown ariaLabel="Completion" value={completionFilter} onChange={setCompletionFilter} options={COMPLETION_OPTIONS} minWidth={140} />
                            <CommonButton type="button" text="Reset" variant="primary" style={{ height: 38 }} onClick={resetFilters} />
                        </div>

                        {filteredResponses.length === 0 ? (
                            <div className="va-empty">
                                <p>No responses match your filter criteria.</p>
                                <CommonButton type="button" text="Clear all filters" variant="secondary" onClick={resetFilters} />
                            </div>
                        ) : (
                            <div>
                                {filteredResponses.map((response, index) => (
                                    <div key={response.id || index} className="va-response-card">
                                        <div className="va-question-category">{formatText(response.question_sub_category || response.sub_category || response.question_category) || 'N/A'}</div>

                                        <div className="va-question-title">
                                            <h5>{assessmentResponses.indexOf(response) + 1}. {response.question_text || 'N/A'}</h5>
                                            <StatusBadge status={response.org_acceptance_status_label || 'Pending'} />
                                        </div>

                                        <div className="mb-3">
                                            <span className="va-field-label">Answer</span>
                                            <div className="va-answer-box">
                                                {response.response_file_url ? (
                                                    <a href={response.response_file_url} target="_blank" rel="noopener noreferrer" className="va-doc-link">View File</a>
                                                ) : (response.question_type === 'multi_select' || response.question_type === 'multi-select' || response.question_type === 'checkbox') ? (
                                                    <div>
                                                        {(() => {
                                                            const selectedOptions = Array.isArray(response.response_text)
                                                                ? response.response_text
                                                                : (response.response_text || '').split(',').map(s => s.trim()).filter(Boolean);
                                                            const allOptions = response.question_multi_select_options || response.options || response.multi_select_options || selectedOptions;
                                                            return allOptions.map((option, idx) => (
                                                                <label key={idx} className="va-checkbox-option">
                                                                    <input type="checkbox" checked={selectedOptions.includes(option)} disabled readOnly />
                                                                    {option}
                                                                </label>
                                                            ));
                                                        })()}
                                                    </div>
                                                ) : (response.question_type === 'yes_no' || response.question_type === 'yes/no') ? (
                                                    <div className="va-radio-row">
                                                        <label className="va-radio-option"><input type="radio" checked={response.response_text?.toLowerCase() === 'yes'} disabled readOnly />Yes</label>
                                                        <label className="va-radio-option"><input type="radio" checked={response.response_text?.toLowerCase() === 'no'} disabled readOnly />No</label>
                                                    </div>
                                                ) : (
                                                    <span>{response.response_text || 'N/A'}</span>
                                                )}
                                            </div>
                                        </div>

                                        {response.explanation && (
                                            <div className="mb-3">
                                                <span className="va-field-label">Explanation</span>
                                                <div className="va-answer-box">{response.explanation}</div>
                                            </div>
                                        )}

                                        {Array.isArray(response.documents) && response.documents.length > 0 && (
                                            <div className="mb-3">
                                                <span className="va-field-label">Evidence</span>
                                                <div className="va-answer-box">
                                                    {response.documents.map((doc, dIdx) => (
                                                        <div key={doc.document_id || dIdx} className="va-evidence-row">
                                                            <span className="va-doc-name">{doc.filename}</span>
                                                            <span className="va-doc-meta">
                                                                {typeof doc.size === 'number' && <span>({(doc.size / 1024).toFixed(1)} KB)</span>}
                                                                {doc.uploaded_at && <span>{new Date(doc.uploaded_at).toLocaleDateString()}</span>}
                                                            </span>
                                                            <CommonActionMenu
                                                                iconOnly
                                                                actions={[
                                                                    { label: 'View', title: 'View', icon: 'preview', onClick: () => handleViewEvidence(response, doc) },
                                                                    { label: 'Download', title: 'Download', icon: 'download', onClick: () => handleDownloadEvidence(response, doc) },
                                                                ]}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {response.is_reviewed && response.reviewed_by_name && (
                                            <div className="mb-3 d-flex gap-2 align-items-center flex-wrap">
                                                <span className="va-field-label mb-0">Reviewed By:</span>
                                                <span className="va-field-value">{response.reviewed_by_name}</span>
                                            </div>
                                        )}

                                        {reviewNotesOf(response) && (() => {
                                            const tone = ACCEPTANCE_TONE[acceptanceOf(response)];
                                            return (
                                                <div className={`va-info-box va-info-box--${tone.box}`}>
                                                    <div className="va-info-box-title"><StatusBadge variant={tone.box}>{tone.label}</StatusBadge></div>
                                                    <div className="va-info-box-body"><strong>Reviewer Notes:</strong> {reviewNotesOf(response)}</div>
                                                </div>
                                            );
                                        })()}

                                        <div className="d-flex justify-content-end mt-3">
                                            <CommonButton
                                                type="button"
                                                text={response.is_reviewed ? 'Update Review' : 'Review'}
                                                variant={response.is_reviewed ? 'secondary' : 'primary'}
                                                disable={reviewing || !REVIEWABLE_STATUSES.includes(assessment?.status)}
                                                onClick={() => handleReviewResponse(response)}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Review Response Modal */}
                    <CommonModal
                        show={showReviewModal}
                        onHide={closeReviewModal}
                        title="Review Response"
                        buttons={[
                            { text: 'Cancel', variant: 'secondary', onClick: closeReviewModal, disabled: reviewing },
                            { text: 'Submit Review', variant: 'success', onClick: handleSubmitReview, loading: reviewing, loadingText: 'Reviewing...' },
                        ]}
                    >
                        {selectedResponse && (
                            <div className="va-modal-field">
                                <div className="va-field-label">Question</div>
                                <p className="va-field-value">{selectedResponse.question_text || 'N/A'}</p>
                                <div className="va-field-label">Response</div>
                                <p className="va-field-value">
                                    {selectedResponse.response_file_url ? (
                                        <a href={selectedResponse.response_file_url} target="_blank" rel="noopener noreferrer" className="va-doc-link">View File</a>
                                    ) : (selectedResponse.response_text || 'N/A')}
                                </p>
                                {selectedResponse.explanation && (
                                    <>
                                        <div className="va-field-label">Explanation</div>
                                        <p className="va-field-value">{selectedResponse.explanation}</p>
                                    </>
                                )}
                            </div>
                        )}
                        <div className="va-modal-field">
                            <label className="va-field-label d-block mb-1">Acceptance Status</label>
                            <CommonDropdown value={acceptanceStatus} onChange={setAcceptanceStatus} options={REVIEW_ACCEPTANCE_OPTIONS} minWidth={0} style={{ width: '100%' }} />
                            {acceptanceStatus === 'follow_up' && (
                                <div className="va-info-box va-info-box--primary mt-2">
                                    <div className="va-info-box-body">
                                        This question will be sent back to the vendor to re-answer. Your review notes below will be shown to them as the reason. Once they resubmit, it returns here as <strong>Pending</strong> for another review.
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="va-modal-field">
                            <label className="va-field-label d-block mb-1">
                                {acceptanceStatus === 'follow_up' ? 'Review Notes (shown to vendor) *' : 'Review Notes'}
                            </label>
                            <textarea className="va-textarea" rows={4} value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} placeholder={acceptanceStatus === 'follow_up' ? 'Explain what the vendor needs to fix or clarify...' : 'Enter your review notes...'} />
                        </div>
                    </CommonModal>

                    {/* Update Assessment Review Status Modal */}
                    <CommonModal
                        show={showAssessmentReviewModal}
                        onHide={closeAssessmentReviewModal}
                        title="Update Assessment Review Status"
                        size="lg"
                        buttons={[
                            { text: 'Cancel', variant: 'secondary', onClick: closeAssessmentReviewModal, disabled: updatingAssessmentStatus },
                            {
                                text: 'Update Status', variant: updateStatusVariant, onClick: handleUpdateAssessmentStatus,
                                loading: updatingAssessmentStatus, loadingText: 'Updating...',
                                disabled: !assessmentReviewStatus || (['approved', 'rejected'].includes(assessmentReviewStatus) && !assessmentGrade),
                            },
                        ]}
                    >
                        <div className="va-modal-field">
                            <label className="va-field-label d-block mb-1">Status *</label>
                            <div className="va-status-picker">
                                {STATUS_PICKER_OPTIONS.map((opt) => {
                                    const active = assessmentReviewStatus === opt.value;
                                    const tone = STATUS_CHIP_TONE[opt.variant];
                                    return (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            className={`va-status-chip${active ? ' va-status-chip--active' : ''}`}
                                            style={active ? { borderColor: tone.fg, background: tone.soft, color: tone.fg } : undefined}
                                            onClick={() => setAssessmentReviewStatus(opt.value)}
                                        >
                                            {opt.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* The --grade modifier is a styling hook: this is the one dropdown in
                            these modals whose open menu is taller than the space beneath it, so
                            the modal reserves a little extra bottom room while it is open. See
                            the `.va-modal-field--grade` rule in VendorAssessmentView.scss. */}
                        {['approved', 'rejected'].includes(assessmentReviewStatus) && (
                            <div className="va-modal-field va-modal-field--grade">
                                {assessment?.last_assessment_grade && <p className="va-field-value mb-1">Last assessment grade: {assessment.last_assessment_grade}</p>}
                                <label className="va-field-label d-block mb-1">Grade *</label>
                                <CommonDropdown value={assessmentGrade} onChange={setAssessmentGrade} options={GRADE_OPTIONS} minWidth={0} style={{ width: '100%' }} />
                            </div>
                        )}

                        <div className="va-modal-field">
                            <label className="va-field-label d-block mb-1">Review Notes</label>
                            <textarea className="va-textarea" rows={4} value={assessmentReviewNotes} onChange={(e) => setAssessmentReviewNotes(e.target.value)} placeholder="Enter review notes about the assessment..." />
                        </div>
                    </CommonModal>

                    {/* Previous Assessment Modal */}
                    <CommonModal
                        show={showPreviousAssessmentModal}
                        onHide={() => setShowPreviousAssessmentModal(false)}
                        title={`Assessment History — ${assessment?.vendor_name || ''}`}
                        size="xl"
                    >
                        {loadingPreviousAssessments ? (
                            <div className="va-loading-row"><ButtonLoader /> Loading...</div>
                        ) : previousAssessments.length === 0 ? (
                            <div className="va-empty">No assessment history found.</div>
                        ) : (
                            <CommonTable
                                // noPadding
                                noScroll
                                headers={['#', 'Questionnaire', 'Status', 'Sent At', 'Expires At', 'Days Left', 'Responses', 'Action']}
                                rows={previousAssessments.map((item, index) => {
                                    const isCurrentAssessment = String(item.id) === String(assessment_id);
                                    return {
                                        id: item.id || index,
                                        // Tint only. The navy `border: 1px solid var(--color-primary)`
                                        // that used to be here drew a hard box around the row — and
                                        // the row already says "current" three other ways: the badge
                                        // beside the questionnaire name, the disabled "Current" action
                                        // button, and this background tint.
                                        rowStyle: isCurrentAssessment ? { backgroundColor: 'var(--color-primary-soft)' } : {},
                                        data: [
                                            index + 1,
                                            <span>
                                                {item.questionnaire_name || 'Default'}
                                                {isCurrentAssessment && <StatusBadge variant="info" className="ms-2">Current</StatusBadge>}
                                            </span>,
                                            <StatusBadge variant={ASSESSMENT_STATUS_VARIANT[item.status]} status={item.status_display || item.status} />,
                                            item.sent_at ? new Date(item.sent_at).toLocaleDateString() : '-',
                                            item.expires_at ? new Date(item.expires_at).toLocaleDateString() : '-',
                                            item.days_left ?? '-',
                                            `${item.completed_responses}/${item.total_questions}`,
                                            <CommonButton
                                                type="button"
                                                text={isCurrentAssessment ? 'Current' : 'View'}
                                                variant={isCurrentAssessment ? 'secondary' : 'primary'}
                                                className="tpcrm-row-btn"
                                                disable={isCurrentAssessment}
                                                onClick={isCurrentAssessment ? undefined : () => window.open(`${process.env.PUBLIC_URL}/vendor/assessment/${item.id}/responses`, '_blank')}
                                            />,
                                        ]
                                    };
                                })}
                            />
                        )}
                    </CommonModal>
                </div>
            </div>
        );
    }

    // Vendor Assessment Mode (Public)
    const allQuestions = [...adminQuestions, ...orgQuestions];

    const vendorCategoryOptions = toOptions(['All', ...new Set(allQuestions.map(q => q.category).filter(Boolean))], 'Category');
    const vendorSubCategoryOptions = toOptions(['All', ...new Set(allQuestions
        .filter(q => selectedCategory === 'All' || q.category === selectedCategory)
        .map(q => q.sub_category)
        .filter(Boolean))], 'Sub-category');

    const filteredQuestions = allQuestions.filter(q => {
        const categoryMatch = selectedCategory === 'All' || q.category === selectedCategory;
        const subCategoryMatch = selectedSubCategory === 'All' || q.sub_category === selectedSubCategory;

        const sourceType = adminQuestions.some(aq => aq.id === q.id) ? 'admin' : 'org';
        const key = `${sourceType}_${q.id}`;
        const textKey = `${sourceType}_text_${q.id}`;
        const isAnswered = (responses[key] && responses[key] !== '' && (!Array.isArray(responses[key]) || responses[key].length > 0)) || (responses[textKey] && responses[textKey].trim() !== '');

        const statusMatch = completionFilter === 'All' ||
            (completionFilter === 'Answered' ? isAnswered : !isAnswered);

        const aiMatch = !showOnlyAiSuggested || aiSuggestions.some(s =>
            s.question_text?.trim().toLowerCase() === q.question_text?.trim().toLowerCase()
        );

        return categoryMatch && subCategoryMatch && statusMatch && aiMatch;
    });

    const totalQuestions = allQuestions.length;
    const completedResponses = assessment?.completed_responses || 0;
    const progressPct = totalQuestions > 0 ? (completedResponses / totalQuestions) * 100 : 0;

    const clearVendorFilters = () => {
        setSelectedCategory('All');
        setSelectedSubCategory('All');
        setCompletionFilter('All');
        setShowOnlyAiSuggested(false);
    };

    return (
        <div className="va-page">
            {confirmModal}
            <div className="va-topbar">
                <img src="https://nmtsecurity-publicbucket.s3.us-west-2.amazonaws.com/dashboard/small_logo.svg" alt="small logo" height={40} width={40} />
                <h3>Foresee Security</h3>
            </div>

            <div className="va-container">
                {/* Header Card */}
                <div className="va-card">
                    <div className="va-card-header">
                        <div>
                            <h2>Vendor Assessment</h2>
                            <p className="va-field-value mb-0">From: {assessment?.created_by_name || 'N/A'}</p>
                        </div>
                        {assessment?.status !== 'sent' && (
                            <StatusBadge variant={ASSESSMENT_STATUS_VARIANT[assessment?.status]} status={assessment?.status || 'N/A'} />
                        )}
                    </div>

                    <div className="va-grid">
                        <div className="va-field"><span className="va-field-label">Vendor Name</span><span className="va-field-value">{assessment?.vendor_name || 'N/A'}</span></div>
                        <div className="va-field"><span className="va-field-label">Vendor Email</span><span className="va-field-value">{assessment?.vendor_email || 'N/A'}</span></div>
                        <div className="va-field"><span className="va-field-label">Questionnaire Name</span><span className="va-field-value">{assessment?.questionnaire_name || 'Default'}</span></div>
                        <div className="va-field">
                            <span className="va-field-label">Expires At</span>
                            <span className="va-field-value">
                                {assessment?.expires_at ? new Date(assessment.expires_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                            </span>
                        </div>
                    </div>

                    <div className="mt-3">
                        <div className="va-progress-row">
                            <span>Progress</span>
                            <span>{completedResponses} / {totalQuestions} Completed</span>
                        </div>
                        <div className="va-progress-track">
                            <div className="va-progress-fill" style={{ width: `${progressPct}%` }} role="progressbar" aria-valuenow={completedResponses} aria-valuemin="0" aria-valuemax={totalQuestions} />
                        </div>
                    </div>
                </div>

                <div className={`va-toolbar${(tutorialStep === 1 || tutorialStep === 2) ? ' tut-toolbar-elevated' : ''}`}>
                    <h4>
                        Assessment Questions
                        <StatusBadge variant="neutral">{totalQuestions} Questions</StatusBadge>
                    </h4>
                    <div className="va-toolbar-actions">
                        <span id="tut-ai-btn" className={tutorialStep === 2 ? ' tut-zone-active' : ''}>
                            <CommonButton
                                type="button"
                                text={loadingSuggestions ? 'Loading...' : 'View AI Suggestions'}
                                variant="primary"
                                disable={loadingSuggestions || isFormDisabled}
                                onClick={() => fetchQuestionSuggestions(true)}
                            />
                        </span>
                        <span id="tut-upload-btn" className={tutorialStep === 1 ? ' tut-zone-active' : ''}>
                            <CommonButton
                                type="button"
                                text="Upload Documents"
                                variant="secondary"
                                disable={isFormDisabled}
                                onClick={() => setShowBulkUploadModal(true)}
                            />
                        </span>
                        <CommonButton
                            type="button"
                            text={submitting ? 'Submitting...' : 'Submit Assessment'}
                            variant="success"
                            disable={submitting || saving || isFormDisabled}
                            onClick={handleFinalSubmit}
                        />
                    </div>
                </div>

                {/* Filter Bar */}
                <div className="va-filters">
                    <CommonDropdown ariaLabel="Category" value={selectedCategory} onChange={(v) => { setSelectedCategory(v); setSelectedSubCategory('All'); }} options={vendorCategoryOptions} minWidth={150} />
                    <CommonDropdown ariaLabel="Sub-category" value={selectedSubCategory} onChange={setSelectedSubCategory} options={vendorSubCategoryOptions} minWidth={150} />
                    <CommonDropdown ariaLabel="Status" value={completionFilter} onChange={setCompletionFilter} options={COMPLETION_OPTIONS} minWidth={140} />
                    <label className="va-checkbox-field">
                        <input type="checkbox" className="va-checkbox" checked={showOnlyAiSuggested} onChange={(e) => setShowOnlyAiSuggested(e.target.checked)} />
                        AI Suggested Only
                    </label>
                    <CommonButton type="button" text="Clear all filters" variant="secondary" onClick={clearVendorFilters} />
                </div>

                {/* Questions Section */}
                <div id="tut-questions" className={tutorialStep === 3 ? 'tut-zone-active' : ''}>
                    {/* Document Upload Status */}
                    {documentUploads.length > 0 && (
                        <div className="va-card">
                            <h4 className="mb-2">Uploaded Documents Status</h4>
                            <div className="d-flex flex-column gap-2">
                                {documentUploads.map((upload, index) => {
                                    const urls = upload.document_urls && upload.document_urls.length > 0 ? upload.document_urls : [null];

                                    return urls.map((url, urlIndex) => {
                                        let fileName = 'Processing Document...';
                                        if (url) {
                                            try {
                                                const urlParts = url.split('/');
                                                fileName = urlParts[urlParts.length - 1];
                                            } catch (e) {
                                                fileName = `Document ${index + 1}`;
                                            }
                                        } else if (upload.file_name) {
                                            fileName = upload.file_name;
                                        }

                                        return (
                                            <div key={`${index}-${urlIndex}`} className="va-doc-row">
                                                <div>
                                                    <div className="va-doc-name">{fileName}</div>
                                                    <div className="va-doc-meta">
                                                        <span>{formatFileSize(upload.total_size_bytes || 0)}</span>
                                                        <span>{new Date(upload.uploaded_at).toLocaleString()}</span>
                                                    </div>
                                                </div>
                                                <StatusBadge variant={upload.status === 'completed' ? 'success' : upload.status === 'processing' ? 'warning' : upload.status === 'failed' ? 'danger' : 'neutral'}>
                                                    {upload.status === 'completed' ? 'Completed' : upload.status === 'processing' ? 'Processing' : upload.status === 'failed' ? 'Failed' : (upload.status || 'Pending')}
                                                </StatusBadge>
                                            </div>
                                        );
                                    });
                                })}
                            </div>
                            {processingStatus === 'processing' && (
                                <div className="d-flex align-items-center gap-2 mt-2" style={{ color: 'var(--color-warning)', fontSize: 'var(--fs-body-sm)' }}>
                                    <ButtonLoader /> AI is analyzing your documents. Suggestions will appear soon...
                                </div>
                            )}
                        </div>
                    )}

                    {filteredQuestions.length === 0 ? (
                        <div className="va-empty">
                            <p>No questions match your filter criteria.</p>
                            <CommonButton type="button" text="Clear all filters" variant="secondary" onClick={clearVendorFilters} />
                        </div>
                    ) : (
                        <div>
                            {filteredQuestions.map((question, index) => {
                                const sourceType = adminQuestions.some(q => q.id === question.id) ? 'admin' : 'org';
                                return (
                                    <div key={question.id} className="va-question-card">
                                        <div className="va-question-category">{formatText(question.sub_category || question.category)}</div>
                                        <div className="va-question-title">
                                            <h6>{index + 1}. {question.question_text}</h6>
                                            {question.is_required && <StatusBadge variant="danger">Required</StatusBadge>}
                                        </div>

                                        {question.expected_evidence && (
                                            <p className="va-question-hint"><strong>Expected Evidence:</strong> {question.expected_evidence}</p>
                                        )}

                                        {question.help_text && (
                                            <p className="va-question-hint"><em>Help: {question.help_text}</em></p>
                                        )}

                                        <div className="mt-4">
                                            {renderInputField(question, sourceType)}
                                        </div>

                                        {(() => {
                                            const suggestion = aiSuggestions.find(s =>
                                                s.question_text?.trim().toLowerCase() === question.question_text?.trim().toLowerCase()
                                            );
                                            const responseKey = `${sourceType}_${question.id}`;
                                            const explanationKey = `${sourceType}_text_${question.id}`;

                                            const targetKey = ['yes_no', 'yes/no', 'multi_select', 'multi-select'].includes((question.response_type || '').toLowerCase())
                                                ? explanationKey
                                                : responseKey;

                                            const currentVal = responses[targetKey] || '';
                                            const isDifferent = (suggestion?.suggested_answer || '').trim() !== String(currentVal || '').trim();

                                            if (suggestion && isDifferent) {
                                                return (
                                                    <div className="va-info-box va-info-box--primary">
                                                        <div className="va-info-box-title">
                                                            <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-primary)' }}>AI Suggestion</span>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <StatusBadge variant={suggestion.confidence >= 80 ? 'success' : suggestion.confidence >= 50 ? 'warning' : 'neutral'}>{suggestion.confidence}% Confidence</StatusBadge>
                                                                <CommonButton
                                                                    type="button"
                                                                    text="Apply"
                                                                    variant="primary"
                                                                    className="tpcrm-row-btn"
                                                                    disable={!suggestion.suggested_answer || !isQuestionEditable(question, sourceType)}
                                                                    onClick={() => applySuggestion(question.id, sourceType, suggestion.suggested_answer, question.response_type)}
                                                                />
                                                            </div>
                                                        </div>
                                                        <p className="va-info-box-body">{suggestion.suggested_answer || 'NA'}</p>
                                                        {suggestion.relevant_excerpts?.length > 0 && (
                                                            <details className="va-excerpts">
                                                                <summary>View source excerpts</summary>
                                                                {suggestion.relevant_excerpts.map((excerpt, i) => (
                                                                    <div key={i}>"{excerpt}"</div>
                                                                ))}
                                                            </details>
                                                        )}
                                                    </div>
                                                );
                                            }
                                            return null;
                                        })()}

                                        {(() => {
                                            const reviewInfo = getReviewInfoForQuestion(question.id, sourceType);
                                            if (reviewInfo) {
                                                // Flagged questions (follow_up / rejected) read as an action box
                                                // so the vendor can tell which ones still need work; accepted /
                                                // pending keep the neutral "Reviewed" success box.
                                                const tone = ACCEPTANCE_TONE[reviewInfo.acceptance] || ACCEPTANCE_TONE.accepted;
                                                const isFlagged = reviewInfo.acceptance === 'follow_up' || reviewInfo.acceptance === 'rejected';
                                                const badgeLabel = isFlagged ? tone.label : 'Reviewed';
                                                return (
                                                    <div className={`va-info-box va-info-box--${tone.box}`}>
                                                        <div className="va-info-box-title">
                                                            <StatusBadge variant={tone.box}>{badgeLabel}</StatusBadge>
                                                            {reviewInfo.reviewed_by_name && <span className="va-question-hint mb-0">by {reviewInfo.reviewed_by_name}</span>}
                                                        </div>
                                                        {reviewInfo.reviewer_notes && <div className="va-info-box-body"><strong>Reviewer Notes:</strong> {reviewInfo.reviewer_notes}</div>}
                                                    </div>
                                                );
                                            }
                                            return null;
                                        })()}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Bulk File Upload Modal */}
                <CommonModal
                    show={showBulkUploadModal}
                    onHide={() => { setShowBulkUploadModal(false); setBulkFiles([]); setBulkUploadMessage(''); }}
                    title="Upload Response Files"
                    size="lg"
                    buttons={[
                        { text: 'Cancel', variant: 'secondary', onClick: () => { setShowBulkUploadModal(false); setBulkFiles([]); setBulkUploadMessage(''); }, disabled: uploadingBulkFiles },
                        {
                            text: `Upload ${bulkFiles.length} File${bulkFiles.length !== 1 ? 's' : ''}`, variant: 'success', onClick: handleBulkUpload,
                            loading: uploadingBulkFiles, loadingText: 'Uploading...', disabled: uploadingBulkFiles || bulkFiles.length === 0,
                        },
                    ]}
                >
                    <div className="va-modal-field">
                        <p className="va-question-hint">
                            Upload files containing your responses to the assessment questions.
                            AI will analyze your documents and suggest answers automatically.
                        </p>
                        <div className="d-flex gap-2 flex-wrap mb-3">
                            <StatusBadge variant="info">Max {MAX_FILES} files</StatusBadge>
                            <StatusBadge variant="info">Max 28MB total</StatusBadge>
                            <StatusBadge variant="neutral">PDF</StatusBadge>
                            <StatusBadge variant="neutral">DOC/DOCX</StatusBadge>
                            <StatusBadge variant="neutral">XLS/XLSX</StatusBadge>
                            <StatusBadge variant="neutral">TXT</StatusBadge>
                        </div>
                        {processingStatus === 'processing' && (
                            <div className="d-flex align-items-center gap-2" style={{ color: 'var(--color-info)', fontSize: 'var(--fs-body-sm)' }}>
                                <ButtonLoader /> AI is analyzing your documents. Suggestions will appear soon...
                            </div>
                        )}
                    </div>

                    <div className="va-modal-field">
                        <label className="va-field-label d-block mb-1">Select Files</label>
                        <input
                            type="file"
                            multiple
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
                            onChange={handleBulkFileSelect}
                            disabled={uploadingBulkFiles || bulkFiles.length >= MAX_FILES}
                        />
                        <div className="va-question-hint mt-1">
                            {bulkFiles.length}/{MAX_FILES} files selected
                            {bulkFiles.length > 0 && ` (Total: ${formatFileSize(bulkFiles.reduce((sum, f) => sum + f.size, 0))})`}
                        </div>
                    </div>

                    {bulkFiles.length > 0 && (
                        <div className="va-modal-field">
                            <div className="va-field-label mb-2">Selected Files</div>
                            <div style={{ maxHeight: 250, overflowY: 'auto' }}>
                                {bulkFiles.map((file, index) => (
                                    <div key={index} className="va-doc-row">
                                        <div>
                                            <div className="va-doc-name">{file.name}</div>
                                            <div className="va-doc-meta"><span>{formatFileSize(file.size)}</span></div>
                                        </div>
                                        <CommonButton type="button" text="✕" variant="error" className="tpcrm-row-btn" disable={uploadingBulkFiles} onClick={() => removeBulkFile(index)} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {bulkUploadMessage && (
                        <StatusBadge variant={bulkUploadMessage.includes('Failed') ? 'danger' : 'success'}>{bulkUploadMessage}</StatusBadge>
                    )}
                </CommonModal>

                {/* Toast Notification */}
                {toast.show && (
                    <div className={`custom-toast ${toast.type}`}>
                        <span className="icon">{toast.type === 'success' ? '✓' : '✕'}</span>
                        <span className="message">{toast.message}</span>
                        <button className="close-btn" onClick={() => setToast({ show: false, message: '', type: 'success' })}>×</button>
                    </div>
                )}

                {/* Help button — restarts tutorial */}
                {!isViewMode && (
                    <button className="tut-help-btn" onClick={() => setTutorialStep(1)} title="Show tutorial">
                        Help
                        <span className="tut-help-icon">?</span>
                    </button>
                )}

                {/* Step-by-step Tutorial */}
                {!isViewMode && tutorialStep && (
                    <>
                        {!showBulkUploadModal && (
                            <div
                                className={`tut-backdrop${tutorialStep === 3 ? ' tut-backdrop--clickable' : ''}`}
                                onClick={tutorialStep === 3 ? closeTutorial : undefined}
                            />
                        )}

                        <div className="tut-card" style={{ top: cardPos.top, left: cardPos.left }}>
                            <div className={`tut-arrow${cardPos.arrowDown ? ' tut-arrow--down' : ''}`} style={{ left: cardPos.arrowLeft }} />

                            <div className="tut-step-header">
                                <span className="tut-step-badge">Step {tutorialStep} of 3</span>
                                <button className="tut-skip" onClick={closeTutorial}>Skip tour</button>
                            </div>
                            <div className="tut-dots">
                                {[1, 2, 3].map(s => (
                                    <span key={s} className={`tut-dot${s === tutorialStep ? ' active' : s < tutorialStep ? ' done' : ''}`} />
                                ))}
                            </div>
                            <h5 className="tut-title">{TUTORIAL_STEPS[tutorialStep - 1].title}</h5>
                            <p className="tut-desc">{TUTORIAL_STEPS[tutorialStep - 1].desc}</p>
                            <div className="tut-actions">
                                <button className="tut-next" onClick={advanceTutorial}>
                                    {tutorialStep === 3 ? 'Got it!' : 'Next →'}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default VendorAssessmentView;
